import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  apiErrorMessage,
  purchaseOrdersService,
  suppliersService,
} from '../../services/api';
import { EmptyState, PageHeader, StatusChip, TableSkeleton } from '../../components/ui';
import { useShop } from '../../contexts/ShopContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  PurchaseOrder,
  PurchaseOrderLinePayload,
  PurchaseOrderStatus,
  SupplierQuote,
  SupplierStatus,
  UserRole,
} from '../../types';
import { APP_LOCALE } from '../../utils/locale';

/**
 * Parts ordering. Three ways in, one purchase order out:
 * - search the connected API suppliers here and order from a cart;
 * - open a punchout supplier's own catalogue (PartsTech), whose cart comes
 *   back as a quote or an order by itself;
 * - record a phone or counter order by hand.
 * Receiving a purchase order is what puts parts on the shelf.
 */

const PAGE_SIZE = 20;
const RECEIVABLE = [
  PurchaseOrderStatus.SUBMITTED,
  PurchaseOrderStatus.CONFIRMED,
  PurchaseOrderStatus.PARTIALLY_RECEIVED,
];

const num = (value: number | string | null | undefined) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const money = (value: number | string | null | undefined) =>
  num(value).toLocaleString(APP_LOCALE, { style: 'currency', currency: 'USD' });

interface CartLine extends PurchaseOrderLinePayload {
  key: string;
  provider: string;
  supplierName: string;
}

interface ManualLine {
  supplierPartNumber: string;
  brand: string;
  description: string;
  quantity: string;
  unitCost: string;
}

const EMPTY_MANUAL_LINE: ManualLine = {
  supplierPartNumber: '',
  brand: '',
  description: '',
  quantity: '1',
  unitCost: '0.00',
};

const PurchaseOrdersPage: React.FC = () => {
  const { selectedShop } = useShop();
  const { user } = useAuth();
  const shopId = user?.role === UserRole.SUPER_ADMIN ? selectedShop?.id : undefined;

  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<SupplierStatus[]>([]);

  // Find parts
  const [findOpen, setFindOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [vin, setVin] = useState('');
  const [results, setResults] = useState<SupplierQuote[]>([]);
  const [searchErrors, setSearchErrors] = useState<{ provider: string; message: string }[]>([]);
  const [searched, setSearched] = useState(false);
  const [cart, setCart] = useState<CartLine[]>([]);

  // Manual order
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSupplier, setManualSupplier] = useState('');
  const [manualLines, setManualLines] = useState<ManualLine[]>([EMPTY_MANUAL_LINE]);

  // Detail
  const [detail, setDetail] = useState<PurchaseOrder | null>(null);
  const [receiveQty, setReceiveQty] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { rows, total: count } = await purchaseOrdersService.list({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        shopId,
      });
      setOrders(rows);
      setTotal(count);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load purchase orders.'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, shopId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    suppliersService
      .list(shopId)
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, [shopId]);

  // A punchout cart is posted back while the supplier window is open; pick it
  // up when the admin returns to this tab.
  useEffect(() => {
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const connected = suppliers.filter(
    (supplier) => supplier.connected && supplier.available && supplier.allowed,
  );
  const searchable = connected.filter((supplier) => supplier.modes.includes('api'));
  const punchout = connected.filter((supplier) => supplier.modes.includes('punchout'));
  const supplierName = (provider: string) =>
    provider === 'manual'
      ? 'Manual'
      : (suppliers.find((supplier) => supplier.provider === provider)?.displayName ?? provider);

  const run = async (action: () => Promise<void>, message?: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      if (message) setNotice(message);
    } catch (err) {
      setError(apiErrorMessage(err, 'Something went wrong.'));
    } finally {
      setBusy(false);
    }
  };

  // --- find parts -----------------------------------------------------------

  const handleSearch = () =>
    run(async () => {
      const looksLikePartNumber = /\d/.test(keyword) && !/\s/.test(keyword.trim());
      const response = await suppliersService.search(
        {
          ...(looksLikePartNumber ? { partNumber: keyword.trim() } : { keyword: keyword.trim() }),
          ...(vin.trim() ? { vehicle: { vin: vin.trim() } } : {}),
        },
        shopId,
      );
      setResults(response.results);
      setSearchErrors(response.errors);
      setSearched(true);
    });

  const addToCart = (quote: SupplierQuote, locationIndex: number) => {
    const location = quote.availability[locationIndex];
    const key = `${quote.provider}:${quote.supplierPartNumber}:${location?.locationId ?? ''}`;
    setCart((current) => {
      const existing = current.find((line) => line.key === key);
      if (existing) {
        return current.map((line) =>
          line.key === key ? { ...line, quantity: line.quantity + 1 } : line,
        );
      }
      return [
        ...current,
        {
          key,
          provider: quote.provider,
          supplierName: quote.supplierName,
          supplierPartNumber: quote.supplierPartNumber,
          brand: quote.brand,
          description: quote.description,
          quantity: 1,
          unitCost: quote.unitCost,
          listPrice: quote.listPrice ?? undefined,
          coreCharge: quote.coreCharge ?? undefined,
          supplierLocation: location?.locationName,
          locationId: location?.locationId,
          quoteRef: quote.quoteRef,
        },
      ];
    });
  };

  const cartByProvider = useMemo(() => {
    const groups = new Map<string, CartLine[]>();
    for (const line of cart) {
      groups.set(line.provider, [...(groups.get(line.provider) ?? []), line]);
    }
    return groups;
  }, [cart]);

  /** One purchase order per supplier, placed straight away. */
  const placeOrders = () =>
    run(async () => {
      const placed: string[] = [];
      for (const [provider, lines] of cartByProvider) {
        const order = await purchaseOrdersService.create(
          {
            supplierProvider: provider,
            supplierName: lines[0].supplierName,
            lines: lines.map(({ key, provider: _p, supplierName: _s, ...line }) => line),
          },
          shopId,
        );
        const submitted = await purchaseOrdersService.submit(order.id);
        placed.push(`${submitted.poNumber} (${submitted.externalOrderId ?? 'placed'})`);
      }
      setCart([]);
      setFindOpen(false);
      await load();
      setNotice(`Ordered: ${placed.join(', ')}`);
    });

  // --- punchout ---------------------------------------------------------------

  const openPunchout = (supplier: SupplierStatus) =>
    run(async () => {
      const { url } = await suppliersService.startPunchout(
        supplier.provider,
        { keyword: keyword.trim() || undefined, vehicle: vin.trim() ? { vin: vin.trim() } : undefined },
        shopId,
      );
      window.open(url, '_blank', 'noopener');
      await load();
    }, `${supplier.displayName} opened in a new window — send the cart back as a quote or an order and it appears here.`);

  // --- manual -----------------------------------------------------------------

  const createManual = () =>
    run(async () => {
      const order = await purchaseOrdersService.create(
        {
          supplierProvider: 'manual',
          supplierName: manualSupplier.trim() || undefined,
          lines: manualLines
            .filter((line) => line.supplierPartNumber.trim())
            .map((line) => ({
              supplierPartNumber: line.supplierPartNumber.trim(),
              brand: line.brand.trim() || undefined,
              description: line.description.trim() || line.supplierPartNumber.trim(),
              quantity: num(line.quantity) || 1,
              unitCost: num(line.unitCost),
            })),
        },
        shopId,
      );
      setManualOpen(false);
      setManualSupplier('');
      setManualLines([EMPTY_MANUAL_LINE]);
      await load();
      openDetail(order);
    }, 'Draft purchase order created');

  // --- detail -----------------------------------------------------------------

  const openDetail = (order: PurchaseOrder) => {
    setDetail(order);
    setReceiveQty(
      Object.fromEntries(
        (order.lines ?? []).map((line) => [
          line.id,
          String(Math.max(0, num(line.quantity) - num(line.quantityReceived))),
        ]),
      ),
    );
  };

  const refreshDetail = async (id: string) => {
    const fresh = await purchaseOrdersService.get(id);
    openDetail(fresh);
    await load();
  };

  const receive = () =>
    run(async () => {
      if (!detail) return;
      const lines = Object.entries(receiveQty)
        .map(([lineId, quantity]) => ({ lineId, quantity: num(quantity) }))
        .filter((line) => line.quantity > 0);
      await purchaseOrdersService.receive(detail.id, lines);
      await refreshDetail(detail.id);
    }, 'Received into stock');

  const detailReceivable = detail ? RECEIVABLE.includes(detail.status) : false;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <PageHeader
        eyebrow={selectedShop?.name}
        title="Parts orders"
        subtitle="Order from your connected suppliers, open a supplier's own catalog, or record a phone order. Receiving an order adds the parts to inventory."
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button onClick={() => setManualOpen(true)}>Manual order</Button>
            {punchout.map((supplier) => (
              <Button
                key={supplier.provider}
                startIcon={<LaunchIcon />}
                disabled={busy}
                onClick={() => openPunchout(supplier)}
              >
                Open {supplier.displayName}
              </Button>
            ))}
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              disabled={searchable.length === 0}
              onClick={() => setFindOpen(true)}
            >
              Find parts
            </Button>
          </Stack>
        }
      />

      {connected.length === 0 && suppliers.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Connect a parts supplier under Integrations to search and order from here. Manual orders
          work without one.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        select
        size="small"
        label="Status"
        value={statusFilter}
        onChange={(event) => {
          setStatusFilter(event.target.value as PurchaseOrderStatus | 'all');
          setPage(0);
        }}
        sx={{ mb: 2, minWidth: 200 }}
      >
        <MenuItem value="all">All</MenuItem>
        {Object.values(PurchaseOrderStatus).map((status) => (
          <MenuItem key={status} value={status}>
            <StatusChip status={status} />
          </MenuItem>
        ))}
      </TextField>

      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {!loading && orders.length === 0 ? (
          <EmptyState
            icon={<LocalShippingIcon />}
            title="No parts orders yet"
            description="Orders you place with suppliers — and carts sent back from a supplier's catalog — show up here."
          />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Lines</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton rows={5} columns={6} />
                ) : (
                  orders.map((order) => (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openDetail(order)}
                    >
                      <TableCell>
                        <Typography fontWeight={600}>{order.poNumber}</Typography>
                        {order.externalOrderId && (
                          <Typography variant="body2" color="text.secondary">
                            Supplier ref {order.externalOrderId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {order.supplierName || supplierName(order.supplierProvider)}
                        <Typography variant="body2" color="text.secondary">
                          via {supplierName(order.supplierProvider)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={order.status} />
                      </TableCell>
                      <TableCell align="right">{order.lines?.length ?? 0}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {money(order.subtotal)}
                      </TableCell>
                      <TableCell>
                        {new Date(num(order.createdAt) * 1000).toLocaleDateString(APP_LOCALE, { dateStyle: 'medium' })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {total > PAGE_SIZE && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(_, next) => setPage(next)}
          />
        )}
      </Paper>

      {/* Find parts */}
      <Dialog open={findOpen} onClose={() => setFindOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Find parts</DialogTitle>
        <DialogContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mt: 1, mb: 2 }}
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSearch();
            }}
          >
            <TextField
              size="small"
              label="Keyword or part number"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              sx={{ flex: 2 }}
              autoFocus
            />
            <TextField
              size="small"
              label="VIN (optional)"
              value={vin}
              onChange={(event) => setVin(event.target.value.toUpperCase())}
              sx={{ flex: 1 }}
            />
            <Button type="submit" variant="contained" disabled={busy || !keyword.trim()}>
              Search
            </Button>
          </Stack>

          {searchErrors.map((failure) => (
            <Alert key={failure.provider} severity="warning" sx={{ mb: 1 }}>
              {supplierName(failure.provider)} didn't answer: {failure.message}
            </Alert>
          ))}

          {searched && results.length === 0 && (
            <Typography color="text.secondary">No parts found at your connected suppliers.</Typography>
          )}

          <Stack spacing={1.5}>
            {results.map((quote) => (
              <Paper key={`${quote.provider}:${quote.supplierPartNumber}`} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={600}>{quote.description}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quote.brand} {quote.supplierPartNumber} · {quote.supplierName}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography fontWeight={600}>{money(quote.unitCost)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quote.listPrice !== null ? `List ${money(quote.listPrice)}` : ''}
                      {quote.coreCharge ? ` · Core ${money(quote.coreCharge)}` : ''}
                    </Typography>
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  {quote.availability.map((location, index) => (
                    <Chip
                      key={location.locationId}
                      icon={<AddIcon />}
                      variant="outlined"
                      color={location.quantity > 0 ? 'primary' : 'default'}
                      label={`${location.locationName}: ${location.quantity} ${location.eta ? `· ${location.eta}` : ''}`}
                      onClick={() => addToCart(quote, index)}
                      disabled={location.quantity <= 0}
                    />
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>

          {cart.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Cart — {cartByProvider.size} order{cartByProvider.size === 1 ? '' : 's'}
              </Typography>
              {cart.map((line) => (
                <Box
                  key={line.key}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}
                >
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
                    {line.brand} {line.supplierPartNumber} — {line.supplierLocation}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={line.quantity}
                    onChange={(event) =>
                      setCart((current) =>
                        current.map((row) =>
                          row.key === line.key
                            ? { ...row, quantity: Math.max(1, num(event.target.value)) }
                            : row,
                        ),
                      )
                    }
                    inputProps={{ min: 1, step: 1 }}
                    sx={{ width: 90 }}
                  />
                  <Typography variant="body2" sx={{ width: 90, textAlign: 'right' }}>
                    {money(line.quantity * line.unitCost)}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Remove from cart"
                    onClick={() => setCart((current) => current.filter((row) => row.key !== line.key))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setFindOpen(false)}>Close</Button>
          <Button variant="contained" disabled={busy || cart.length === 0} onClick={placeOrders}>
            {busy ? 'Placing…' : `Place order${cartByProvider.size > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual */}
      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manual order</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Supplier"
              value={manualSupplier}
              onChange={(event) => setManualSupplier(event.target.value)}
              placeholder="Store you ordered from by phone or at the counter"
            />
            {manualLines.map((line, index) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1.1fr 0.9fr 1.6fr 70px 100px 40px' },
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                {(['supplierPartNumber', 'brand', 'description', 'quantity', 'unitCost'] as const).map(
                  (key) => (
                    <TextField
                      key={key}
                      size="small"
                      label={
                        index === 0
                          ? {
                              supplierPartNumber: 'Part number',
                              brand: 'Brand',
                              description: 'Description',
                              quantity: 'Qty',
                              unitCost: 'Cost',
                            }[key]
                          : undefined
                      }
                      type={key === 'quantity' || key === 'unitCost' ? 'number' : 'text'}
                      value={line[key]}
                      onChange={(event) =>
                        setManualLines((current) =>
                          current.map((row, i) =>
                            i === index ? { ...row, [key]: event.target.value } : row,
                          ),
                        )
                      }
                    />
                  ),
                )}
                <IconButton
                  aria-label="Remove line"
                  disabled={manualLines.length === 1}
                  onClick={() => setManualLines((current) => current.filter((_, i) => i !== index))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setManualLines((current) => [...current, EMPTY_MANUAL_LINE])}
              >
                Add line
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setManualOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !manualLines.some((line) => line.supplierPartNumber.trim())}
            onClick={createManual}
          >
            Create draft
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail */}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {detail.poNumber}
              <StatusChip status={detail.status} />
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {detail.supplierName || supplierName(detail.supplierProvider)} via{' '}
                {supplierName(detail.supplierProvider)}
                {detail.externalOrderId ? ` · supplier ref ${detail.externalOrderId}` : ''}
              </Typography>
              {detail.lastError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {detail.lastError}
                </Alert>
              )}
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Part</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell align="right">Ordered</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell align="right">Cost</TableCell>
                      {detailReceivable && <TableCell align="right">Receive now</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.lines ?? []).map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {line.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {line.brand} {line.supplierPartNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>{line.supplierLocation || '—'}</TableCell>
                        <TableCell align="right">{num(line.quantity)}</TableCell>
                        <TableCell align="right">{num(line.quantityReceived)}</TableCell>
                        <TableCell align="right">{money(line.unitCost)}</TableCell>
                        {detailReceivable && (
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={receiveQty[line.id] ?? '0'}
                              onChange={(event) =>
                                setReceiveQty((current) => ({
                                  ...current,
                                  [line.id]: event.target.value,
                                }))
                              }
                              inputProps={{
                                min: 0,
                                max: num(line.quantity) - num(line.quantityReceived),
                                step: 1,
                              }}
                              sx={{ width: 90 }}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography sx={{ mt: 2, textAlign: 'right' }} fontWeight={600}>
                Subtotal {money(detail.subtotal)}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: 'wrap', gap: 1 }}>
              {[
                PurchaseOrderStatus.DRAFT,
                PurchaseOrderStatus.SUBMITTED,
                PurchaseOrderStatus.CONFIRMED,
                PurchaseOrderStatus.FAILED,
              ].includes(detail.status) &&
                !(detail.lines ?? []).some((line) => num(line.quantityReceived) > 0) && (
                  <Tooltip title="Cancels it here only — call the supplier if it was already placed">
                    <span>
                      <Button
                        color="error"
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            await purchaseOrdersService.cancel(detail.id);
                            await refreshDetail(detail.id);
                          }, 'Purchase order canceled')
                        }
                      >
                        Cancel order
                      </Button>
                    </span>
                  </Tooltip>
                )}
              <Box sx={{ flex: 1 }} />
              <Button onClick={() => setDetail(null)}>Close</Button>
              {detail.status === PurchaseOrderStatus.DRAFT && (
                <Button
                  variant="contained"
                  disabled={busy || !(detail.lines ?? []).length}
                  onClick={() =>
                    run(async () => {
                      await purchaseOrdersService.submit(detail.id);
                      await refreshDetail(detail.id);
                    }, detail.supplierProvider === 'manual' ? 'Marked as ordered' : 'Order placed')
                  }
                >
                  {detail.supplierProvider === 'manual' ? 'Mark as ordered' : 'Place order'}
                </Button>
              )}
              {detailReceivable && (
                <Button variant="contained" disabled={busy} onClick={receive}>
                  Receive into stock
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        message={notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Box sx={{ height: 24 }} />
    </Container>
  );
};

export default PurchaseOrdersPage;
